/**
 * useHasTags — есть ли у организации хотя бы один действующий QR-код наклейки.
 *
 * Гейт для входов в сканер («Сканировать наклейку»): пока наклейки не выпущены
 * (или все отозваны), сканировать нечего, и кнопка на самом видном месте
 * экрана — шум. Источник — GET /equipment/tags/stats, `total` там считает
 * только неотозванные коды.
 *
 * Кэш на модуле (три входа в сканер читают один ответ), подписчики
 * обновляются при invalidateHasTags() — после выпуска партии и отзыва кода.
 *
 * ⚠️ Ошибка сети → показываем (fail-open): потерять сканер из-за сбоя хуже,
 * чем показать лишнюю кнопку. Пока грузится — прячем, чтобы кнопка не мигала.
 * `enabled=false` (нет equipment.view) — не ходим в сеть вовсе.
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client';

let cache = null;            // Promise<boolean>|null
const subs = new Set();

function load() {
  if (!cache) {
    cache = api.equipmentTagStats()
      .then((s) => (s?.total || 0) > 0)
      .catch(() => true);
  }
  return cache;
}

/** Сбросить кэш и перечитать — после выпуска партии / отзыва кода. */
export function invalidateHasTags() {
  cache = null;
  if (subs.size) load().then((v) => subs.forEach((fn) => fn(v)));
}

export function useHasTags({ enabled = true } = {}) {
  const [has, setHas] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const fn = (v) => { if (alive) setHas(v); };
    subs.add(fn);
    load().then(fn);
    return () => { alive = false; subs.delete(fn); };
  }, [enabled]);

  return enabled && has;
}
